import React, { useReducer } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  sitInRest,
  sitOutRest,
  sitInAllTablesRest,
  sitOutNextHandAllTablesRest,
  sitOutNextBBRest,
  sitOutNextBBAllTablesRest,
} from '@/services/table/tableService';
import Checkbox from '@/components/new-design/Checkbox';
import { useToast } from '@/components/toast/useToast';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import buttonStyles from '@/components/new-design/Button.module.css';
import { Combobox, ComboboxButton, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import styles from './SitOutButton.module.css';
import ChevronDownIcon from '@/components/ChevronDownIcon';
import ChevronUpIcon from '@/components/ChevronUpIcon';
import useIsMobileView from '@/hooks/useIsMobileView';

interface SitOutButtonProps {
  tableId: string;
  disabled?: boolean;
}

interface SitoutTypeOption {
  label: string;
  slug: string;
  allTables?: boolean;
}

type ActionType = { type: 'TOGGLE_CHECKBOX' } | { type: 'SET_OPTION'; payload: SitoutTypeOption };

const SitoutTypes = {
  sitoutNextHand: {
    label: 'Sit Out Next Hand',
    slug: 'sit-out-next-hand',
  },
  sitoutNextBB: {
    label: 'Sit Out Next BB',
    slug: 'sit-out-next-bb',
  },
  sitoutNextHandAllTables: {
    label: 'Sit Out Next Hand - All Tables',
    slug: 'sit-out-next-hand-all-tables',
    allTables: true,
  },
  sitoutNextHandBBAllTables: {
    label: 'Sit Out Next BB - All Tables',
    slug: 'sit-out-next-bb-all-tables',
    allTables: true,
  },
};

interface StateType {
  isChecked: boolean;
  selectedOption: SitoutTypeOption;
}

const initialState: StateType = {
  isChecked: false,
  selectedOption: SitoutTypes.sitoutNextHand,
};

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'TOGGLE_CHECKBOX':
      return { ...state, isChecked: !state.isChecked };
    case 'SET_OPTION':
      return { ...state, selectedOption: action.payload, isChecked: false };
    default:
      return state;
  }
};

const SitOutButton: React.FC<SitOutButtonProps> = ({ tableId, disabled }) => {
  const isMobileView = useIsMobileView();
  const [state, dispatch] = useReducer(reducer, initialState);
  const tableState = useSelector((state: ReducerState) => state.tables?.[tableId]);
  const userTables = useSelector((state: ReducerState) =>
    Object.values(state.tables).map((table) => ({
      tableId: table.tableId,
      tableUrl: table.url,
    })),
  );

  const { toast } = useToast();

  const sitIn = async () => {
    if (state.selectedOption.allTables) {
      await sitInAllTablesRest(userTables);
      return;
    }
    await sitInRest({ tableId, tableUrl: tableState?.url, sitoutType: state.selectedOption.slug });
  };

  const handleOnClickSitOutTable = async () => {
    try {
      dispatch({ type: 'TOGGLE_CHECKBOX' });

      if (state.isChecked) {
        sitIn();
      } else {
        if (state.selectedOption.slug === SitoutTypes.sitoutNextHand.slug) {
          await sitOutRest({ tableId, tableUrl: tableState?.url });
        }

        if (state.selectedOption.slug === SitoutTypes.sitoutNextBB.slug) {
          await sitOutNextBBRest({ tableId, tableUrl: tableState?.url });
        }

        if (state.selectedOption.slug === SitoutTypes.sitoutNextHandAllTables.slug) {
          await sitOutNextHandAllTablesRest(userTables);
        }

        if (state.selectedOption.slug === SitoutTypes.sitoutNextHandBBAllTables.slug) {
          await sitOutNextBBAllTablesRest(userTables);
        }
      }
    } catch (err) {
      toast({ description: 'Error sitting out the table' });
      Sentry.captureException(err);
      alert('Error when leaving table');
    }
  };

  const handleSelectChange = (value: SitoutTypeOption) => {
    dispatch({ type: 'SET_OPTION', payload: value });

    if (state.isChecked) {
      sitIn();
    }
  };

  return (
    <Combobox value={state.selectedOption} onChange={handleSelectChange} disabled={disabled}>
      {({ open, disabled }) => {
        return (
          <div className={twMerge('relative', isMobileView && 'h-full')}>
            <div
              className={twMerge(
                buttonStyles.pillWithBorder,
                styles.sitOutSelect,
                open && styles.open,
                state.isChecked && styles.checked,
                disabled && styles.disabled,
              )}
            >
              <div className="relative flex w-full justify-between">
                <Checkbox
                  text={state.selectedOption.label}
                  checked={state.isChecked}
                  onChange={handleOnClickSitOutTable}
                  className="text-white hover:cursor-pointer"
                  disabled={disabled}
                />
                <ComboboxButton className="-mr-4 flex items-center p-2 pr-4 text-ph-white-30">
                  {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </ComboboxButton>
              </div>
            </div>
            <ComboboxOptions className={twMerge(styles.sitOutOptions, '!bg-ph-background-dark')}>
              <ComboboxOption value={SitoutTypes.sitoutNextHand} className={styles.sitOutOption}>
                {SitoutTypes.sitoutNextHand.label}
              </ComboboxOption>
              <ComboboxOption value={SitoutTypes.sitoutNextBB} className={styles.sitOutOption}>
                {SitoutTypes.sitoutNextBB.label}
              </ComboboxOption>
              <ComboboxOption value={SitoutTypes.sitoutNextHandAllTables} className={styles.sitOutOption}>
                {SitoutTypes.sitoutNextHandAllTables.label}
              </ComboboxOption>
              <ComboboxOption value={SitoutTypes.sitoutNextHandBBAllTables} className={styles.sitOutOption}>
                {SitoutTypes.sitoutNextHandBBAllTables.label}
              </ComboboxOption>
            </ComboboxOptions>
          </div>
        );
      }}
    </Combobox>
  );
};

export default SitOutButton;
